import type {
	App,
	Directive,
	DirectiveBinding,
} from 'vue';

const HANDLERS_PROPERTY = '__v-click-outside';

type HTMLElementWithVClickOutside = (
	HTMLElement
	& {
		[HANDLERS_PROPERTY]?: {
			event: 'click';
			srcTarget: HTMLElement;
			handler: (event: HTMLElementEventMap['click']) => void;
		};
	}
);
type VClickOutsideBeforeMountDirective = (el: HTMLElementWithVClickOutside, binding: Pick<DirectiveBinding<((...args: any[]) => any)>, 'value'>) => void;
type VClickOutsideUnmountedDirective = (el: HTMLElementWithVClickOutside) => void;
type VClickOutsideUpdatedDirective = (el: HTMLElementWithVClickOutside, binding: Pick<DirectiveBinding<((...args: any[]) => any)>, 'oldValue' | 'value'>) => void;

interface OnEventParams {
	el: HTMLElementWithVClickOutside;
	event: HTMLElementEventMap['click'];
	handler: ((...args: any[]) => any);
}

function validate(bindingValue: ((...args: any[]) => any)): boolean {
	if (typeof bindingValue !== 'function') {
		console.warn('v-click-outside: Binding value must be a function');
		return false;
	}

	return true;
}

function onEvent(params: OnEventParams): void {
	const {
		el,
		event,
		handler,
	} = params;
	const path = (
		(
			'path' in event
			&& event.path
		)
		|| event.composedPath?.()
	) as EventTarget[] | undefined;
	const eventTarget = event.target as HTMLElement | null;

	if (eventTarget) {
		if (
			path
			&& path.length > 0
		) {
			path.unshift(eventTarget);
		}

		if (
			el.contains(eventTarget)
			|| el.isSameNode(eventTarget)
			|| event.defaultPrevented
		) {
			return;
		}

		handler(event);
	}
}

const beforeMount: VClickOutsideBeforeMountDirective = (
	el,
	{
		value,
	},
) => {
	if (!validate(value)) {
		return;
	}

	el[HANDLERS_PROPERTY] = {
		event: 'click',
		srcTarget: document.documentElement,
		handler: (event) => onEvent({
			el,
			event,
			handler: value,
		}),
	};
	const {
		event,
		srcTarget,
		handler,
	} = el[HANDLERS_PROPERTY];
	setTimeout(
		() => {
			if (!el[HANDLERS_PROPERTY]) {
				return;
			}

			srcTarget.addEventListener(
				event,
				handler,
				{
					passive: false,
				},
			);
		},
		0,
	);
};
const unmounted: VClickOutsideUnmountedDirective = (el) => {
	if (el[HANDLERS_PROPERTY]) {
		const {
			event,
			srcTarget,
			handler,
		} = el[HANDLERS_PROPERTY];
		srcTarget.removeEventListener(
			event,
			handler,
		);
		delete el[HANDLERS_PROPERTY];
	}
};
const updated: VClickOutsideUpdatedDirective = (
	el,
	{
		value,
		oldValue,
	},
) => {
	if (JSON.stringify(value) === JSON.stringify(oldValue)) {
		return;
	}

	if (!validate(value)) {
		return;
	}

	unmounted(el);
	beforeMount(
		el,
		{
			value,
		},
	);
};
const directive: Directive = {
	beforeMount,
	updated,
	unmounted,
};

const plugin = {
	install(app: App): void {
		app.directive(
			'click-outside',
			directive,
		);
	},
	directive,
};

export default plugin;
